import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Shield,
  AlertTriangle,
  Bug,
  Scan,
  Activity,
  Clock,
  Target,
  TrendingUp,
  Filter,
  RefreshCw,
  ChevronRight,
  Loader2,
  ShieldAlert,
  Zap,
  Eye,
  FileWarning
} from 'lucide-react';
import api from '../lib/api';

export default function VulnerabilityDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ severity: '', status: '', attack_type: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, vulnsRes] = await Promise.all([
        api.get('/vulnerabilities/dashboard/stats'),
        api.get('/vulnerabilities', { params: { limit: 10, ...filter } })
      ]);
      setStats(statsRes.data);
      setVulnerabilities(vulnsRes.data);
    } catch (error) {
      toast.error('Failed to load vulnerability data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-black',
      low: 'bg-blue-500 text-white',
      info: 'bg-slate-400 text-white'
    };
    return colors[severity] || colors.info;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-red-100 text-red-700 border-red-200',
      in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      mitigated: 'bg-blue-100 text-blue-700 border-blue-200',
      resolved: 'bg-green-100 text-green-700 border-green-200',
      accepted_risk: 'bg-purple-100 text-purple-700 border-purple-200',
      false_positive: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return styles[status] || styles.open;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'zero_day': return <Zap className="w-4 h-4 text-red-500" />;
      case 'emergent': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'critical_window': return <Clock className="w-4 h-4 text-yellow-500" />;
      default: return <Bug className="w-4 h-4 text-slate-500" />;
    }
  };

  if (loading && !stats) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vulnerability-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Vulnerability Management</h1>
            <p className="text-slate-500 mt-1">Track, assess, and remediate security vulnerabilities</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => navigate('/vulnerability-scanner')} data-testid="scan-btn">
              <Scan className="w-4 h-4 mr-2" />
              Run Scan
            </Button>
            <Button variant="outline" onClick={() => navigate('/continuous-monitoring')} data-testid="monitoring-btn">
              <Activity className="w-4 h-4 mr-2" />
              Monitoring
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="border-l-4 border-l-slate-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.total_vulnerabilities}</p>
                    <p className="text-xs text-slate-500">Total Vulns</p>
                  </div>
                  <Shield className="w-8 h-8 text-slate-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-red-600">{stats.critical_count}</p>
                    <p className="text-xs text-slate-500">Critical</p>
                  </div>
                  <ShieldAlert className="w-8 h-8 text-red-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-orange-600">{stats.high_count}</p>
                    <p className="text-xs text-slate-500">High</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">{stats.medium_count}</p>
                    <p className="text-xs text-slate-500">Medium</p>
                  </div>
                  <FileWarning className="w-8 h-8 text-yellow-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{stats.zero_day_count}</p>
                    <p className="text-xs text-slate-500">Zero-Day</p>
                  </div>
                  <Zap className="w-8 h-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{stats.overdue_remediations}</p>
                    <p className="text-xs text-slate-500">Overdue</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Attack Type Distribution */}
        {stats && stats.vulnerabilities_by_attack_type && Object.keys(stats.vulnerabilities_by_attack_type).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vulnerabilities by Attack Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {Object.entries(stats.vulnerabilities_by_attack_type).map(([type, count]) => (
                  <div
                    key={type}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                    onClick={() => setFilter({ ...filter, attack_type: type })}
                  >
                    <Target className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-medium capitalize">{type.replace(/_/g, ' ')}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filter Vulnerabilities
              </CardTitle>
              {(filter.severity || filter.status || filter.attack_type) && (
                <Button variant="ghost" size="sm" onClick={() => setFilter({ severity: '', status: '', attack_type: '' })}>
                  Clear Filters
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <select
                value={filter.severity}
                onChange={(e) => setFilter({ ...filter, severity: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="severity-filter"
              >
                <option value="">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="status-filter"
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="mitigated">Mitigated</option>
                <option value="resolved">Resolved</option>
                <option value="accepted_risk">Accepted Risk</option>
              </select>

              <select
                value={filter.attack_type}
                onChange={(e) => setFilter({ ...filter, attack_type: e.target.value })}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="attack-type-filter"
              >
                <option value="">All Attack Types</option>
                <option value="phishing">Phishing</option>
                <option value="ransomware">Ransomware</option>
                <option value="ddos">DDoS</option>
                <option value="mitm">MITM</option>
                <option value="sql_injection">SQL Injection</option>
                <option value="xss">XSS</option>
                <option value="zero_day_exploit">Zero-Day Exploit</option>
                <option value="dns_spoofing">DNS Spoofing</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Vulnerabilities List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Recent Vulnerabilities</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/vulnerabilities/all')}>
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {vulnerabilities.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Bug className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No vulnerabilities found</p>
                  <p className="text-sm mt-1">Run a scan to discover vulnerabilities</p>
                </div>
              ) : (
                vulnerabilities.map((vuln) => (
                  <div
                    key={vuln.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => navigate(`/vulnerabilities/${vuln.id}`)}
                    data-testid={`vuln-item-${vuln.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {getCategoryIcon(vuln.category)}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900">{vuln.title}</h3>
                          {vuln.cve_id && (
                            <Badge variant="outline" className="text-xs">{vuln.cve_id}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          {vuln.attack_type?.replace(/_/g, ' ')} • {new Date(vuln.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getSeverityColor(vuln.severity)}>
                        {vuln.severity}
                      </Badge>
                      <Badge variant="outline" className={getStatusBadge(vuln.status)}>
                        {vuln.status?.replace(/_/g, ' ')}
                      </Badge>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/vulnerability-scanner')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Scan className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Vulnerability Scanner</h3>
                  <p className="text-sm text-slate-500">Scan assets for vulnerabilities</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/continuous-monitoring')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Continuous Monitoring</h3>
                  <p className="text-sm text-slate-500">Real-time threat detection</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/risks')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Risk Register</h3>
                  <p className="text-sm text-slate-500">View auto-logged risks</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
